
import { Download, FileText } from 'lucide-react';

export default function PayrollCard({ payroll }) {
  // Dummy fallback data for preview
  const data = payroll || {
    month: 'July',
    employeeName: 'Hashib Raja',
    employeeId: 'EMP123',
    designation: 'Senior Developer',
    department: 'Engineering',
    baseSalary: 5000,
    allowances: 2000,
    deductions: 1000,
    netPay: 6000,
  };

  return (
    <div className="bg-white/90 dark:bg-gray-800/80 backdrop-blur-sm dark:backdrop-blur-lg rounded-2xl shadow-xl p-8 max-w-xl mx-auto transition-all duration-500">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-6">
        <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mb-2 animate-fade-in-up">{data.month} Payslip</h2>
        <div className="grid grid-cols-2 gap-4 animate-fade-in-up">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Employee Name</p>
            <p className="font-semibold text-gray-800 dark:text-gray-100">{data.employeeName}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Employee ID</p>
            <p className="font-semibold text-gray-800 dark:text-gray-100">{data.employeeId}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Department</p>
            <p className="font-semibold text-gray-800 dark:text-gray-100">{data.department}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Designation</p>
            <p className="font-semibold text-gray-800 dark:text-gray-100">{data.designation}</p>
          </div>
        </div>
      </div>

      {/* Salary Details */}
      <div className="space-y-6 animate-fade-in-up">
        <div className="grid grid-cols-2 gap-4">
          {/* Basic Information */}
          <div className="flex flex-col">
            <span className="text-gray-500 dark:text-gray-400">Base Salary</span>
            <span className="font-semibold text-lg text-gray-800 dark:text-gray-100">${data.baseSalary}</span>
          </div>

          {/* Allowances */}
          <div className="flex flex-col">
            <span className="text-gray-500 dark:text-gray-400">Total Allowances</span>
            <span className="font-semibold text-lg text-emerald-600 dark:text-emerald-400">${data.allowances}</span>
          </div>

          {/* Allowance Details */}
          {data.allowanceDetails && (
            <div className="col-span-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Allowance Breakdown</h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(data.allowanceDetails).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">${value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deductions */}
          <div className="flex flex-col">
            <span className="text-gray-500 dark:text-gray-400">Total Deductions</span>
            <span className="font-semibold text-lg text-rose-600 dark:text-rose-400">-${data.deductions}</span>
          </div>

          {/* Overtime */}
          {data.overtime && (
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Overtime ({data.overtime.hours}hrs)</span>
              <span className="font-semibold text-lg text-emerald-600 dark:text-emerald-400">${data.overtime.amount}</span>
            </div>
          )}

          {/* Deduction Details */}
          {data.deductionDetails && (
            <div className="col-span-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Deduction Breakdown</h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(data.deductionDetails).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="font-medium text-rose-600 dark:text-rose-400">-${value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Net Pay */}
          <div className="col-span-2 mt-4">
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400">Net Pay</span>
              <span className="font-bold text-xl bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 text-indigo-700 dark:text-indigo-200 px-4 py-2 rounded-lg shadow-inner">${data.netPay}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4 mt-8 animate-fade-in-up">
        <button className="flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow hover:from-indigo-700 hover:to-purple-700 dark:from-indigo-700 dark:to-purple-700 dark:hover:from-indigo-600 dark:hover:to-purple-600 transition-all duration-200 transform hover:scale-[1.02]">
          <Download className="h-5 w-5" />
          Download PDF
        </button>
        <button className="flex items-center gap-2 px-5 py-3 rounded-lg bg-white/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-indigo-700 dark:text-indigo-200 font-semibold shadow hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 transform hover:scale-[1.02]">
          <FileText className="h-5 w-5" />
          View Payslip
        </button>
      </div>
    </div>
  );
}
